import {Component, computed, inject, input, Input, output} from "@angular/core";
import {FontAwesomeCode, ParticipantType, SimUser} from "@models";
import {Translations} from "@translations";
import {SimMaterialDialogService} from "@services";
import {ListItemComponent} from "../list-item.component";
import {MatIcon} from "@angular/material/icon";
import {MatIconButton} from "@angular/material/button";
import {StopPropagationDirective} from "@common/pipes/stop-propagation.directive";

@Component({
	selector: "sim-user-list-item",
	templateUrl: "./user-list-item.component.html",
	styleUrls: ["./user-list-item.component.scss"],
	imports: [ListItemComponent, MatIcon, MatIconButton, StopPropagationDirective],
})
export class UserListItemComponent {
	// I/O
	@Input() selected: boolean = false;
	@Input() deletable: boolean = false;
	@Input() showOnClickDialog: boolean = true;
	@Input() secondaryLabel: string | null = null;
	@Input() secondaryLabelIcon: FontAwesomeCode | null = null;
	onDelete = output<SimUser>();
	onSelect = output<SimUser>();

	// Signals
	public user = input.required<SimUser>();
	public indicatorType = input<ParticipantType | "initials">("initials");
	protected userInitials = computed<string>(() => this._computeUserInitials());
	protected userIcon = computed<FontAwesomeCode | null>(() => this._computeUserIcon());

	// Readonly
	protected readonly T = Translations;

	// Injections
	private _simDialogService: SimMaterialDialogService = inject(SimMaterialDialogService);

	public showUserCard(): void {
		this._simDialogService
			.openUserCardMatDialog({
				userData: this.user(),
			})
			.then();
	}

	public deleteClicked(): void {
		this.onDelete.emit(this.user());
	}

	public onClick(event: MouseEvent): void {
		this.showOnClickDialog ? this.showUserCard() : this.onSelect.emit(this.user());
		event.preventDefault();
	}

	private _computeUserInitials(): string {
		if (this.user().firstname && this.user().lastname) {
			return this.user().firstname.charAt(0) + this.user().lastname.charAt(0);
		} else {
			return this.user().fullname?.charAt(0) ?? this.user().username?.charAt(0) ?? "";
		}
	}

	private _computeUserIcon(): FontAwesomeCode | null {
		const result = {fontSet: "fa"} as FontAwesomeCode;
		switch (this.indicatorType()) {
			case "initials":
				return null;
			case ParticipantType.Instructor:
				result.fontIcon = "fa-user-md";
				break;
			case ParticipantType.Learner:
				result.fontIcon = "fa-user-graduate";
				break;
			case ParticipantType.StandardizedPatient:
				result.fontIcon = "fa-user-injured";
		}
		return result;
	}
}
